/**
 * A chord grid turned into something playable.
 *
 * The output is the same shape a part's uploaded MIDI is stored in (see the note at the top of
 * midiJson.js) even though none of this ever gets saved. That shape is what every piece of the
 * playback path already speaks: flattenMidiNotes feeds the scheduler, and MidiPiano draws the
 * keyboard straight off it. Inventing a second note format for the preview would mean teaching
 * both of those about it for no gain.
 */

import { BEAT_EPSILON, cellChordBeats, isMelodyCell } from './chordBars';
import { getIntensityLevel, getLayerGain, INTENSITY_LEVELS, LAYER_ON, STRONG } from './chartIntensity';
import { BAND_LAYERS, getBandNotes } from './chordBand';
import { getPadNotes, getPadSound } from './chordPad';
import { getChordNotes } from './chordNotes';
import { getRhythmBassPair } from './chordRhythm';
import {
  ARP,
  BASS,
  FIFTH,
  getBlockStyle,
  getPadSoundId,
  getPatternSteps,
  getRhythmPattern,
  hasBassLine,
  hasBandLayer,
  LEFT_HAND,
  playsBassAt,
  ROOT,
  TONE,
} from './chordRhythmPatterns';
import { getPitchClass, voiceChordBody } from './chordVoicing';
import { getBeatsPerBar } from './songTiming';

// Root notes are voiced into this octave — C3 up to B3 — which puts a chord's body in the middle
// of every sampled instrument's range rather than at an edge where the recordings thin out.
const ROOT_OCTAVE_MIDI = 48;

// A slash chord's bass sits an octave under the chord it carries, which is where a player would
// put it: low enough to read as the bass, close enough to still be the same chord.
const BASS_OCTAVE_MIDI = ROOT_OCTAVE_MIDI - 12;

// The bass player's roots sit from the guitar's open low E up to the D# above it
const BASS_GUITAR_LOW_MIDI = 28;

/** What a style's bass line is played on. */
export const BASS_INSTRUMENT_ID = 'sf-electric-bass-finger';

// A melody line starts around middle C, over the chords, and is kept within two octaves of it
const MELODY_START_MIDI = 60;
const MELODY_LOW_MIDI = 48;
const MELODY_HIGH_MIDI = 84;

// Even velocity throughout. This is a chart being checked, not a performance — an accent pattern
// would be the preview inventing musical detail the grid never stated.
const VELOCITY = 0.8;

// Chords are released fractionally before the next one so a change is audible as a change rather
// than as one chord blurring into the next through the sampler's release tail.
const DETACH = 0.02;

/** How long a note of a given span actually sounds, once it has been let go of in time. */
const soundingDuration = (span) => Math.max(span - DETACH, span / 2);

/**
 * Where one chord's notes sit — the body of it, without whatever the bass is doing underneath.
 *
 * Exported so the clip builder can hold on to the voicing it got back and hand it in as the next
 * chord's `previous`: chordVoicing.js has no idea what came before, and a chord on its own has no
 * answer to that. Given nothing, a chord voices from its root exactly as it always has, which is
 * what a single tapped chord gets.
 */
export const getChordVoicing = (chord, previous = null) => {
  const { root, intervals, inversion } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return [];

  return voiceChordBody(intervals, rootClass, ROOT_OCTAVE_MIDI, { inversion, previous });
};

/**
 * The one note a symbol plays in a melody bar: its root, in whichever octave is nearest the note
 * before it — a line moves by step far more often than it leaps.
 *
 * @returns {number|null} A MIDI number, or null for a symbol with no readable root
 */
export const getMelodyNote = (chord, previous = null) => {
  const pitch = getPitchClass(getChordNotes(chord).root);

  if (pitch === null) return null;
  if (previous === null) return MELODY_START_MIDI + pitch;

  const up = (pitch - (previous % 12) + 12) % 12;
  const note = up <= 6 ? previous + up : previous + up - 12;

  if (note > MELODY_HIGH_MIDI) return note - 12;
  if (note < MELODY_LOW_MIDI) return note + 12;

  return note;
};

/** The MIDI numbers one chord symbol sounds as. */
export const getChordMidiNotes = (chord, { previous = null } = {}) => {
  const notes = getChordVoicing(chord, previous);

  if (notes.length === 0) return [];

  const { bassNote } = getChordNotes(chord);
  const bassClass = bassNote ? getPitchClass(bassNote) : null;

  // A slash chord still gains its bass note, and an inverted chord still gains nothing: '/' asks
  // for a note under the chord, '^' asks only that the chord be turned over.
  return bassClass === null ? notes : [BASS_OCTAVE_MIDI + bassClass, ...notes];
};

/**
 * The two MIDI numbers a chord's rhythm bass alternates between.
 *
 * Separate from getChordRhythmNotes because a keyboard drawing what a chord *is* has no bar to put
 * it in — it wants the pitches, not a performance of them. The octave lives here rather than at the
 * call site so there is one answer to where the bass sits.
 *
 * The same pair whichever style is picked: every pattern draws its bass from chordRhythm.js, and
 * what they differ in is when it is struck, which a resting keyboard has nothing to say about. The
 * arpeggio reaches an octave over the root as well, and that note doesn't light up here — a run is a
 * thing you hear rather than a note the chord is made of.
 */
export const getChordRhythmBass = (chord) => getRhythmBassPair(chord, BASS_OCTAVE_MIDI) || [];

/**
 * The run of notes an arpeggio walks: up the chord, on to the root an octave above, then back down
 * through the middle of it.
 *
 * Turning round at the top rather than dropping straight back to the bottom is what keeps a roll
 * sounding like one line rather than a figure restarting over and over.
 */
const getArpRun = (chordMidi) => {
  const up = [...chordMidi, chordMidi[0] + 12];

  return [...up, ...up.slice(1, -1).reverse()];
};

/**
 * One chord played rather than simply sounded: the hands taking turns, in whichever pattern was
 * picked.
 *
 * The pattern says when and for how long; this says what — the bass pair from chordRhythm.js, the
 * chord itself, or the next note of its arpeggio. Splitting it there means a new style is a few
 * lines of data in chordRhythmPatterns.js rather than a branch in here.
 *
 * The two hands take turns because that is what makes a rhythm audible at all: a chord held over a
 * moving bass is still ringing when the bass moves, so the bass never lands against anything.
 *
 * The held chord drops the bass note getChordMidiNotes puts under a slash chord. That bass is what
 * the pattern starts on, and holding it as well would be the same note in two places doing two
 * different things.
 *
 * Shared by the preview clip and by a single tapped chord, which is why the time is an offset with a
 * default rather than a position in a bar — the tap adds the audio clock's now to it.
 *
 * A pattern's `lead` steps lean into `next`, the chord that follows straight on, and are voiced
 * from it. With none, the figure plays its own way through to the end.
 *
 * @param {string} chord - A chord symbol
 * @param {object} options - { patternId, time, beats, beatsPerBar, secondsPerBeat, velocity, previous, next, thin,
 *   intensity }
 * @returns {Array<{midi: number, time: number, duration: number, velocity: number}>}
 */
export const getChordRhythmNotes = (
  chord,
  {
    patternId,
    time = 0,
    beats,
    beatsPerBar,
    secondsPerBeat,
    velocity = VELOCITY,
    previous = null,
    next = null,
    bassist = false,
    thin = false,
    intensity,
  } = {},
) => {
  // Null for a symbol with no readable root, which is the same symbol getChordVoicing has nothing
  // to say about — one guard covers both hands
  const pair = getRhythmBassPair(chord, BASS_OCTAVE_MIDI);

  if (pair === null) return [];

  const chordMidi = getChordVoicing(chord, previous);
  const arpRun = getArpRun(chordMidi);
  // Voiced near this one when voicing is on, the way the chord itself is
  const nextMidi = next ? getChordVoicing(next, previous ? chordMidi : null) : [];
  const nextPair = next ? getRhythmBassPair(next, BASS_OCTAVE_MIDI) : null;
  const leading = nextMidi.length > 0 && nextPair !== null;
  const notes = [];

  const pattern = getRhythmPattern(patternId);

  const strike = (midi, at, hold, level) => {
    const note = {
      midi,
      time: time + at * secondsPerBeat,
      duration: soundingDuration(hold * secondsPerBeat),
      velocity: velocity * level,
    };

    // Under the damper a note goes on ringing after the hand leaves it, to the end of the chord and
    // no further — the pedal comes up at the change, or two harmonies smear into one. `duration`
    // stays what was actually played, which is what the on-screen keyboard draws: the key lifts on
    // time while the sound carries, exactly as it does for an uploaded clip that was pedalled.
    if (pattern.pedal) note.sustain = soundingDuration((beats - at) * secondsPerBeat);

    notes.push(note);
  };

  // The bass walks on across the whole chord, so a pattern striking it once a bar plays the root in
  // one bar and its fifth in the next. The arpeggio starts again each cycle instead — see the note
  // on the two in chordRhythmPatterns.js.
  let bassStep = 0;
  let arpStep = 0;
  let arpCycle = 0;
  // Whether the harmony itself was played, rather than only the bass under it
  let sounded = false;

  const steps = getPatternSteps(pattern, { beats, beatsPerBar, thin, intensity, leading });

  steps.forEach(({ at, play, hold, level, tone, double, octave, alter, lead, cycle }) => {
    // The bass player has the low end, so the left hand stays off it
    if (bassist && LEFT_HAND.includes(play)) return;

    const voiced = lead ? nextMidi : chordMidi;
    const bassPair = lead ? nextPair : pair;

    if (play === BASS) {
      strike(bassPair[bassStep % bassPair.length], at, hold, level);
      bassStep += 1;

      return;
    }

    // A figure that says which of the two it wants, rather than taking whichever is next. The pair
    // is [root, the fifth below it], so naming them is reading it in order.
    if (play === ROOT || play === FIFTH) {
      strike(play === ROOT ? bassPair[0] : bassPair[1], at, hold, level);

      return;
    }

    sounded = true;

    // A note of the chord asked for by degree. A triad asked for a seventh wraps back round to its
    // root rather than being given one it hasn't got — see the note on TONE in chordRhythmPatterns.js
    if (play === TONE) {
      const midi = voiced[tone % voiced.length] + octave * 12 + alter;

      strike(midi, at, hold, level);

      // The octave above, struck with it, so the two read as one thickened line rather than as a
      // chord — which is what an octave figure is
      if (double) strike(midi + 12, at, hold, level);

      return;
    }

    if (play === ARP) {
      if (cycle !== arpCycle) {
        arpCycle = cycle;
        arpStep = 0;
      }

      strike(arpRun[arpStep % arpRun.length], at, hold, level);
      arpStep += 1;

      return;
    }

    voiced.forEach((midi) => strike(midi, at, hold, level));
  });

  // A chord written shorter than the first answer in its pattern leaves the bass playing alone.
  // It is sounded with that bass instead: dropping it would lose the harmony altogether, which is
  // the one thing the chart is being played to check.
  if (!sounded && beats > 0) chordMidi.forEach((midi) => strike(midi, 0, beats, 1));

  return notes;
};

/**
 * One chord's share of a style's bass line, on the bass guitar — empty for a style with none.
 *
 * The root (a slash chord's bass) sits from E1 up. `TONE` counts up from it to the degree asked
 * for; `FIFTH` reaches down to the fifth, or up an octave from there where that is under E1.
 *
 * @param {string} chord - A chord symbol
 * @param {object} options - { patternId, pattern, time, beats, beatsPerBar, secondsPerBeat, velocity, thin, intensity }.
 *   `pattern` is played in place of the one `patternId` names: a block style.
 * @returns {Array<{midi: number, time: number, duration: number, velocity: number}>}
 */
export const getChordBassNotes = (
  chord,
  { patternId, pattern: given, time = 0, beats, beatsPerBar, secondsPerBeat, velocity = VELOCITY, thin = false, intensity } = {},
) => {
  const pattern = given || getRhythmPattern(patternId);
  const { root, intervals, bassNote } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (!hasBassLine(pattern) || rootClass === null) return [];

  const bassClass = (bassNote ? getPitchClass(bassNote) : null) ?? rootClass;
  const low = BASS_GUITAR_LOW_MIDI + ((bassClass - (BASS_GUITAR_LOW_MIDI % 12) + 12) % 12);
  const [, fifth] = getRhythmBassPair(chord, low - bassClass);

  const pitchOf = ({ play, tone, octave, alter }) => {
    if (play === FIFTH) return fifth < BASS_GUITAR_LOW_MIDI ? fifth + 12 : fifth;
    if (play === TONE) return low + ((rootClass + intervals[tone % intervals.length] - bassClass + 12) % 12) + octave * 12 + alter;

    return low;
  };

  return getPatternSteps({ cycle: pattern.cycle, steps: pattern.bass }, { beats, beatsPerBar, thin, intensity }).map((step) => ({
    midi: pitchOf(step),
    time: time + step.at * secondsPerBeat,
    duration: soundingDuration(step.hold * secondsPerBeat),
    velocity: velocity * step.level,
  }));
};

/**
 * The grid as a flat run of bars, which is what it is musically — the line breaks are how the
 * chart is laid out on the page, not pauses in the playing.
 */
const flattenBars = (lines) => (lines || []).flatMap((cells) => cells || []).map((cell) => String(cell ?? '').trim());

/**
 * Every strike in the chart, in order, with the second it lands on.
 *
 * A tie is not a strike: its beats go on the end of the chord it holds rather than starting another
 * one, which is the same reading cellChords takes when it drops ties from a bar. So this list is
 * getChartChords' list, chord for chord — the player lights the nth chord of a chart by lighting the
 * nth strike, and the sound and the highlight cannot drift because they are the same pass.
 *
 * A rest is not a strike either. It cuts the chord before it off where it starts, and a tie after it
 * holds the silence rather than bringing the chord back. An empty bar is a tie across the whole bar.
 *
 * Read before any rhythm or voicing is chosen, so neither moves a chord's moment.
 *
 * @param {string[][]} lines - The grid, as chordBars.js models it
 * @param {object} song - { bpm, timeSignature }
 * @returns {Array<{ chord: string, time: number, beats: number, melody?: boolean }>} Empty where
 *   there is no tempo. `melody` for a strike in a melody bar, which plays as a single note.
 */
export const getChordStrikes = (lines, { bpm, timeSignature } = {}) => {
  const tempo = Number(bpm);

  if (!tempo || tempo <= 0) return [];

  const beatsPerBar = getBeatsPerBar(timeSignature);
  const secondsPerBar = (beatsPerBar * 60) / tempo;
  const secondsPerBeat = secondsPerBar / beatsPerBar;
  const strikes = [];
  // Whether a rest is what is "sounding", so a tie after it stays silent
  let silent = false;

  flattenBars(lines).forEach((cell, atBar) => {
    const entries = cellChordBeats(cell, beatsPerBar);
    const melody = isMelodyCell(cell);

    // Several chords written into one bar share *that bar*, a beat at a time — "| B A C |" in 4/4
    // is the three changes landing on clicks 1, 2 and 3, and "| C / / G |" is C on click 1 and G on
    // click 4. A lone chord holds the bar and comes back with no beats of its own.
    const held = entries.length > 0 ? entries : [{ chord: null, beats: beatsPerBar }];
    const lengths = held.map(({ beats }) => beats ?? beatsPerBar);
    const written = lengths.reduce((total, length) => total + length, 0);

    // A bar written short of its beats is closed by its last chord, the way formatCell writes it out
    if (written < beatsPerBar - BEAT_EPSILON) lengths[lengths.length - 1] += beatsPerBar - written;

    let beat = 0;

    held.forEach(({ chord, rest }, index) => {
      const time = atBar * secondsPerBar + beat * secondsPerBeat;

      beat += lengths[index];

      if (rest) {
        silent = true;

        return;
      }

      // A tie is not a strike. The chord it holds was struck in a bar before this one, so its beats
      // go on the end of that note rather than starting another — playing it again would be a
      // restrike, which is exactly what tying it over the bar line says it is not. With nothing
      // before it there is nothing to hold, and the gap it leaves is the same one a rest leaves.
      if (chord === null) {
        if (!silent && strikes.length > 0) strikes[strikes.length - 1].beats += lengths[index];

        return;
      }

      silent = false;
      strikes.push({ chord, time, beats: lengths[index], ...(melody ? { melody } : null) });
    });
  });

  return strikes;
};

const scaleVelocity = (note, scale) => ({ ...note, velocity: Math.min(1, note.velocity * scale) });

// The octave above the top note of each chord struck, so a Full part rings out over the rest.
// A single note — a melody bar, an arpeggio — is a line, and stays one.
const doubleTops = (notes, melodyTimes) => {
  const byTime = new Map();

  notes.forEach((note) => {
    if (!melodyTimes.has(note.time)) byTime.set(note.time, [...(byTime.get(note.time) || []), note]);
  });

  const tops = [...byTime.values()]
    .filter((struck) => struck.length > 1)
    .map((struck) => struck.reduce((top, note) => (note.midi > top.midi ? note : top)));

  return [...notes, ...tops.map((top) => ({ ...top, midi: top.midi + 12 }))].sort((a, b) => a.time - b.time);
};

/**
 * A preview clip for a chord grid.
 *
 * @param {string[][]} lines - The grid, as chordBars.js models it
 * @param {object} song - { bpm, timeSignature, rhythm, voicing }. `rhythm` is the id of the pattern
 *   to play each chord with — see chordRhythmPatterns.js — and empty for the block chords the grid
 *   states outright. One value rather than a flag beside an id: rhythm mode *is* the choice of a
 *   pattern. `voicing` lets each chord be voiced near the one before it rather than from its root.
 *   `bass` brings in the bass player on a style that has a bass line: a second track, 'Bass', and
 *   the piano's left hand dropped.
 *   `intensity` is a level out of chartIntensity.js: it scales both hands, thins the figure at the
 *   quiet levels, and at Sparse sends the bass player out.
 *   `pad` brings in the held chord a style names, as a third track, 'Pad', where the level has one.
 *   `padHeld` overrides the level: true plays the pad at a level without one (at Strong's velocity,
 *   the run turning it down), false leaves it out.
 *   `band` lists the extra players to bring in (BAND_LAYERS ids), each on a style that has it, as a
 *   track of its own name, where the level has one.
 *   `layers` is the part's `chartLayers`: the bass and band players it switches on or off whatever
 *   the level (getLayerGain).
 *   `category` is the song's: block chords take their bass, pad and band from its block style
 *   (getBlockStyle), the piano still playing the chords as written.
 *   `then` is the chord played straight after the chart, for a style's last bar to lead into: the
 *   part's own first chord on a repeat, the next part's first on the last pass.
 *   `rises` is whether what comes after the chart plays louder, which the violin swells into, and
 *   `following` that part's strikes, each with its `end` in seconds, for a landing to ring on into.
 *   `variation` moves where the violin's figures start taking turns, so each pass plays its own.
 * @returns {object|null} MIDI JSON, or null when the grid has nothing to play
 */
export const buildChordBarsMidi = (
  lines,
  {
    bpm,
    timeSignature,
    rhythm = '',
    voicing = false,
    bass = false,
    pad = false,
    padHeld,
    band = [],
    layers = null,
    intensity,
    category = '',
    then = null,
    rises = false,
    following = [],
    variation = 0,
  } = {},
) => {
  const tempo = Number(bpm);

  if (!tempo || tempo <= 0) return null;

  const beatsPerBar = getBeatsPerBar(timeSignature);
  const bars = flattenBars(lines);
  const strikes = getChordStrikes(lines, { bpm, timeSignature });

  if (strikes.length === 0) return null;

  const secondsPerBar = (beatsPerBar * 60) / tempo;
  const notes = [];
  const bassNotes = [];
  const level = getIntensityLevel(intensity);
  // What the bass, pad and band play off: the style, or under block chords the block style
  const layered = rhythm ? getRhythmPattern(rhythm) : getBlockStyle(beatsPerBar, category);
  const bassGain = getLayerGain(layers, 'bass', level.id);
  const bassist = Boolean(bass && bassGain > 0 && hasBassLine(layered));
  // Switched in at a level its line has nothing for, the bass plays the nearest level's line
  const bassLevel =
    layers?.bass === LAYER_ON && !playsBassAt(layered, level.id)
      ? (INTENSITY_LEVELS.map(({ id }) => id)
          .filter((id) => playsBassAt(layered, id))
          .sort((a, b) => Math.abs(a - level.id) - Math.abs(b - level.id))[0] ?? level.id)
      : level.id;
  const padId = pad && (padHeld ?? level.pad > 0) ? getPadSoundId(layered) : null;
  const padVelocity = level.pad || getIntensityLevel(STRONG).pad;
  const padSound = getPadSound(padId);
  const bandPlaying = BAND_LAYERS.filter(
    ({ id }) => band.includes(id) && getLayerGain(layers, id, level.id) > 0 && hasBandLayer(layered, id),
  );
  const melodyTimes = new Set();

  const secondsPerBeat = secondsPerBar / beatsPerBar;

  // The voicing the last chord was played in, so the next one can be put near it. Carried across
  // the whole chart rather than reset per line: the line breaks are how the chart is laid out on the
  // page, not pauses in the playing, which is the same reading flattenBars takes. Left null while
  // the option is off, and a chord given no previous voices from its root exactly as it always did.
  let previous = null;
  // The last note a melody bar played, so the next one lands near it
  let lastNote = null;

  // The chord that comes in where this one ends, if one does: not across a rest, not a melody note
  const nextChord = (index, end) => {
    const following = strikes[index + 1];

    if (!following) return end >= bars.length * secondsPerBar - BEAT_EPSILON * secondsPerBeat ? then : null;

    return !following.melody && Math.abs(following.time - end) < BEAT_EPSILON * secondsPerBeat ? following.chord : null;
  };

  strikes.forEach(({ chord, time, beats, melody }, index) => {
    // A melody bar is a line of single notes, played plain whatever the rhythm, and leaves the
    // chords' voicing where it was
    if (melody) {
      const midi = getMelodyNote(chord, lastNote);

      if (midi !== null) {
        notes.push({ midi, time, duration: soundingDuration(beats * secondsPerBeat), velocity: VELOCITY });
        melodyTimes.add(time);
        lastNote = midi;
      }

      return;
    }

    // A tie has already been folded into the beats of the chord it holds, so a held chord simply
    // keeps the pattern running under it rather than starting a second one
    if (rhythm) {
      const span = {
        patternId: rhythm,
        time,
        beats,
        beatsPerBar,
        secondsPerBeat,
        thin: Boolean(level.thin),
        intensity: level.id,
      };

      const next = nextChord(index, time + beats * secondsPerBeat);

      getChordRhythmNotes(chord, { ...span, previous, next, bassist }).forEach((note) => notes.push(note));

      if (bassist) getChordBassNotes(chord, { ...span, intensity: bassLevel }).forEach((note) => bassNotes.push(note));
    } else {
      const duration = soundingDuration(beats * secondsPerBeat);

      getChordMidiNotes(chord, { previous }).forEach((midi) =>
        notes.push({ midi, time, duration, velocity: VELOCITY }),
      );

      if (bassist) {
        getChordBassNotes(chord, {
          pattern: layered,
          time,
          beats,
          beatsPerBar,
          secondsPerBeat,
          intensity: bassLevel,
        }).forEach((note) => bassNotes.push(note));
      }
    }

    // Last, so `previous` is the chord before this one everywhere above. An unreadable symbol
    // voices as nothing and leaves the hand where it was, rather than resetting the run.
    if (voicing) {
      const voiced = getChordVoicing(chord, previous);

      if (voiced.length > 0) previous = voiced;
    }
  });

  const [numerator, denominator = 4] = String(timeSignature || '4/4').split('/');
  const played = (level.double ? doubleTops(notes, melodyTimes) : notes).map((note) => scaleVelocity(note, level.piano));
  const bassPlayed = bassNotes.map((note) => scaleVelocity(note, bassGain));
  const padPlayed = padSound
    ? getPadNotes(strikes, { secondsPerBeat, velocity: VELOCITY * padVelocity, top: level.double, pulse: padSound.pulse })
    : [];
  const bandTracks = bandPlaying.map(({ id, name, instrument }) => ({
    name,
    instrument,
    notes: getBandNotes(id, strikes, {
      pattern: layered,
      beatsPerBar,
      secondsPerBeat,
      velocity: VELOCITY * getLayerGain(layers, id, level.id),
      intensity: level.id,
      thin: Boolean(level.thin),
      top: level.double,
      bars: bars.length,
      then,
      rises,
      following,
      variation,
    }),
  }));

  return {
    name: 'Chord bars',
    bpm: tempo,
    timeSignature: [parseInt(numerator, 10) || 4, parseInt(denominator, 10) || 4],
    duration: bars.length * secondsPerBar,
    tracks: [
      { name: 'Chords', instrument: '', notes: played },
      ...(bassist ? [{ name: 'Bass', instrument: BASS_INSTRUMENT_ID, notes: bassPlayed }] : []),
      ...(padSound ? [{ name: 'Pad', instrument: padId, notes: padPlayed }] : []),
      ...bandTracks,
    ],
  };
};
